import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Flag, Home, ListChecks } from 'lucide-react';
import Map, { MapRef, Marker, NavigationControl, Popup } from 'react-map-gl';

import { MAP_BOX_KEY } from '../../config/config';
import {
  COURSE_PIN_WIDTH,
  COURSE_PIN_HEIGHT,
  coursePinScaleForZoom,
} from '../../config/exploreMapLayers';
import { CourseImageConstants } from '../../constants/CourseImageConstants';

import 'mapbox-gl/dist/mapbox-gl.css';

type PointKind = 'home' | 'played' | 'bucket';

interface MapPoint {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  kind: PointKind;
}

const KIND_LABEL: Record<PointKind, string> = {
  home: 'Home course',
  played: 'Played',
  bucket: 'Bucket list',
};

// One badge per kind, echoing the sidebar's own icons for the three member pages. `Home` rather
// than the sidebar's MapPin: a map-pin glyph sitting inside a map pin reads as a mistake.
const KIND_ICON: Record<
  PointKind,
  React.ComponentType<{ className?: string }>
> = {
  home: Home,
  played: Flag,
  bucket: ListChecks,
};

interface CourseLocation {
  id?: string;
  courseName?: string;
  courseLatitude?: number;
  courseLongitude?: number;
}

interface MemberCoursesMapProps {
  homeCourses: CourseLocation[];
  coursesPlayed: CourseLocation[];
  bucketList?: CourseLocation[];
}

// Room for the legend (top-left), the zoom controls (top-right) and the attribution along the
// bottom, so no pin lands underneath any of them. maxZoom stops two courses at the same club
// fitting to a zero-width box and slamming the camera to maximum zoom, i.e. a grey square.
const FIT_OPTIONS = {
  padding: { top: 56, bottom: 40, left: 48, right: 56 },
  maxZoom: 12,
};

// The dashboard's map, on the same footing as the public Explore map: the brand Monochrome
// basemap, the globe projection, and the same GP_Course pin art at the same zoom-driven size.
// It stays a plain set of DOM markers rather than Explore's clustered symbol layer, because a
// member's own list is a handful of courses and not 41,119.
//
// The three kinds are told apart by a badge on the pin, not by recolouring it. Recolouring is
// what the previous version did (gold / black / blue), and the blue was #3B82F6 — a Tailwind
// colour that is not in the brand pack at all. A gold pin means "golf course" everywhere else in
// this product, so the pin keeps its colour and the badge carries the state.
const MemberCoursesMap: React.FC<MemberCoursesMapProps> = ({
  homeCourses,
  coursesPlayed,
  bucketList = [],
}) => {
  const [selected, setSelected] = useState<MapPoint | null>(null);

  const points: MapPoint[] = useMemo(() => {
    const toPoints = (courses: CourseLocation[], kind: PointKind): MapPoint[] =>
      courses
        .filter((c) => c.courseLatitude != null && c.courseLongitude != null)
        .map((c) => ({
          id: `${kind}-${c.id}`,
          name: c.courseName || KIND_LABEL[kind],
          latitude: c.courseLatitude as number,
          longitude: c.courseLongitude as number,
          kind,
        }));

    return [
      ...toPoints(homeCourses, 'home'),
      ...toPoints(coursesPlayed, 'played'),
      ...toPoints(bucketList, 'bucket'),
    ];
  }, [homeCourses, coursesPlayed, bucketList]);

  // Frame the member's actual courses. This used to centre on their first home course at a fixed
  // zoom of 3, which is a whole continent: a member whose courses all sit in one province got
  // three overlapping dots somewhere in the middle of it.
  //
  // The fit runs in an effect rather than through `initialViewState`, because home courses,
  // played courses and the bucket list arrive from three separate queries. react-map-gl reads
  // initialViewState once, at mount, so whichever list answered first decided the camera and the
  // pins that landed afterwards fell outside the frame.
  const mapRef = useRef<MapRef | null>(null);

  // Only the coordinates matter, and only when the set of them changes. Without this key the
  // effect would refit on every render and drag the member back to the start each time they
  // panned the map.
  const pointsKey = points
    .map((p) => `${p.id}:${p.latitude},${p.longitude}`)
    .join('|');

  useEffect(() => {
    const map = mapRef.current;
    if (!map || points.length === 0) {
      return;
    }

    if (points.length === 1) {
      map.easeTo({
        center: [points[0].longitude, points[0].latitude],
        zoom: 9,
        duration: 0,
      });
      return;
    }

    const lats = points.map((p) => p.latitude);
    const lngs = points.map((p) => p.longitude);

    map.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      { ...FIT_OPTIONS, duration: 0 },
    );
    // FIT_OPTIONS is a module-level constant in all but name; `points` is covered by pointsKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointsKey]);

  // Tracked only so the pins scale with zoom the way Explore's do. The map itself stays
  // uncontrolled: feeding viewState back in would make every pan a React render.
  //
  // The seed is a mid-scale guess for the frames before the fit lands; `onLoad` and `onZoom`
  // then report what the camera actually settled on.
  const [zoom, setZoom] = useState(8);
  const pinScale = coursePinScaleForZoom(zoom);

  if (points.length === 0) {
    return (
      <div className="flex h-96 items-center justify-center rounded-lg border border-dashed border-gray-300 text-sm text-ink-muted dark:border-white/20 dark:text-white/50">
        No courses with a location yet.
      </div>
    );
  }

  return (
    <div className="member-courses-map relative h-96 w-full overflow-hidden rounded-lg">
      <Map
        ref={mapRef}
        initialViewState={{ latitude: 20, longitude: 0, zoom: 1.2 }}
        onLoad={(evt) => setZoom(evt.target.getZoom())}
        onZoom={(evt) => setZoom(evt.viewState.zoom)}
        mapboxAccessToken={MAP_BOX_KEY || ''}
        style={{ width: '100%', height: '100%' }}
        // The same brand "Monochrome" style Explore uses (account: golfplayed). It is
        // account-scoped, so the token in .env.local has to belong to that account or this
        // renders blank — the same constraint Explore documents.
        mapStyle="mapbox://styles/golfplayed/clws4surt011b01qx6qk794sk"
        // Globe, like Explore. A member with courses on two continents gets the same read here
        // as they do there, instead of Mercator's stretched high latitudes.
        projection={{ name: 'globe' }}
      >
        <NavigationControl position="top-right" showCompass={false} />

        {points.map((point) => {
          const Icon = KIND_ICON[point.kind];

          return (
            <Marker
              key={point.id}
              latitude={point.latitude}
              longitude={point.longitude}
              // The pin is a teardrop: its tip is the location. Matches Explore's
              // `icon-anchor: bottom`, without which every course floats half a pin north.
              anchor="bottom"
              onClick={(event) => {
                event.originalEvent.stopPropagation();
                setSelected(point);
              }}
            >
              <div className="relative cursor-pointer">
                <img
                  src={CourseImageConstants.COURSE_PIN}
                  alt=""
                  style={{
                    width: COURSE_PIN_WIDTH * pinScale,
                    height: COURSE_PIN_HEIGHT * pinScale,
                  }}
                />
                {/* Black disc, gold glyph: 9.05:1, and it stays legible against both the gold
                    pin under it and the dark basemap around it. */}
                <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-secondary ring-1 ring-primary">
                  <Icon className="h-2.5 w-2.5 text-primary" />
                </span>
              </div>
            </Marker>
          );
        })}

        {selected && (
          <Popup
            latitude={selected.latitude}
            longitude={selected.longitude}
            closeOnClick={false}
            onClose={() => setSelected(null)}
            offset={28}
          >
            <p className="text-sm font-semibold text-white">{selected.name}</p>
            <p className="text-xs text-primary">{KIND_LABEL[selected.kind]}</p>
          </Popup>
        )}
      </Map>

      {/* Top-left. Mapbox keeps its logo bottom-left and its attribution bottom-right, and
          NavigationControl has top-right, so this is the only free corner. */}
      <div className="absolute left-2 top-2 flex gap-3 rounded-md border border-primary/25 bg-charcoal/90 px-2 py-1.5 text-xs text-white/80 backdrop-blur-sm">
        {(Object.keys(KIND_LABEL) as PointKind[]).map((kind) => {
          const Icon = KIND_ICON[kind];

          return (
            <span key={kind} className="flex items-center gap-1">
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-secondary ring-1 ring-primary">
                <Icon className="h-2.5 w-2.5 text-primary" />
              </span>
              {KIND_LABEL[kind]}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default MemberCoursesMap;
